import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, BookOpen, GraduationCap, Users, FileText,
  HelpCircle, ArrowRight, ShoppingBag,
  Video, ChevronRight,
} from 'lucide-react';
import { fetchAds } from '../services/api';
import SplashScreen from '../components/SplashScreen';

// ── Animation helpers ──────────────────────────────────────────────────────────
const fadeUp  = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } };
const stagger = { show: { transition: { staggerChildren: 0.09 } } };

const Divider = () => (
  <div className="max-w-7xl mx-auto px-6">
    <div className="h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
  </div>
);

// ── Static data ────────────────────────────────────────────────────────────────
const SYLLABUSES = [
  {
    name: 'Local Syllabus', path: '/local',     tag: 'Sri Lanka',
    accent:    'from-green-500/15 to-green-900/5',
    border:    'border-green-500/20',
    badgeColor:'bg-green-500/10 text-green-400 border-green-500/20',
    iconColor: 'text-green-400',
    levels: ['O/L', 'A/L'],
    desc: 'Past papers, notes and resources for the Sri Lanka national curriculum.',
  },
  {
    name: 'Cambridge',      path: '/cambridge', tag: 'International',
    accent:    'from-blue-500/15 to-blue-900/5',
    border:    'border-blue-500/20',
    badgeColor:'bg-blue-500/10 text-blue-400 border-blue-500/20',
    iconColor: 'text-blue-400',
    levels: ['IGCSE', 'AS / A-Level'],
    desc: 'Comprehensive resources for Cambridge IGCSE and A-Level examinations.',
  },
  {
    name: 'Edexcel',        path: '/edexcel',   tag: 'Pearson',
    accent:    'from-orange-500/15 to-orange-900/5',
    border:    'border-orange-500/20',
    badgeColor:'bg-orange-500/10 text-orange-400 border-orange-500/20',
    iconColor: 'text-orange-400',
    levels: ['IGCSE', 'IAL'],
    desc: 'Edexcel past papers, mark schemes and revision material for all levels.',
  },
];

const SERVICES = [
  { name: 'Find a Tutor',    path: '/tutors',   icon: Search,      color: 'text-accentBlue', bg: 'bg-accentBlue/10', desc: 'Connect with expert tutors'       },
  { name: 'Online Classes',  path: '/local',    icon: Video,       color: 'text-purple-400', bg: 'bg-purple-500/10', desc: 'Live sessions via Zoom & Meet'    },
  { name: 'Private Classes', path: '/private',  icon: Users,       color: 'text-green-400',  bg: 'bg-green-500/10',  desc: 'One-on-one learning sessions'     },
  { name: 'Shop',            path: '/shop',     icon: ShoppingBag, color: 'text-yellow-400', bg: 'bg-yellow-500/10', desc: 'Books, papers & study materials'  },
  { name: 'Blog',            path: '/blog',     icon: FileText,    color: 'text-pink-400',   bg: 'bg-pink-500/10',   desc: 'Tips, guides and study hacks'     },
  { name: 'Help & Support',  path: '/support',  icon: HelpCircle,  color: 'text-cyan-400',   bg: 'bg-cyan-500/10',   desc: "We're here to help you"           },
];

// ─────────────────────────────────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();
  const [ads,        setAds]        = useState([]);
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem('splashShown'));


  useEffect(() => {
    fetchAds().then(res => setAds(res.data || [])).catch(console.error);
  }, []);

  return (
    <>
      <AnimatePresence>
        {showSplash && (
          <SplashScreen finishLoading={() => { sessionStorage.setItem('splashShown', '1'); setShowSplash(false); }} />
        )}
      </AnimatePresence>

      <div className="overflow-x-hidden">

        {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
        <section className="relative flex items-center justify-center px-6 pt-20 pb-16 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-[-8%] left-[8%]   w-[480px] h-[480px] bg-accentBlue/7   rounded-full blur-[120px]" />
            <div className="absolute bottom-[0%] right-[4%] w-[360px] h-[360px] bg-purple-500/7  rounded-full blur-[100px]" />
            <div className="absolute top-[50%] left-[48%]  w-[280px] h-[280px] bg-green-500/4   rounded-full blur-[100px]" />
          </div>

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.h1
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-white leading-[1.08] mb-5"
            >
              Elevate Your<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accentBlue via-blue-300 to-purple-400">
                Learning Journey
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-gray-400 text-lg sm:text-xl max-w-xl mx-auto mb-8 leading-relaxed"
            >
              Past papers, resources and expert tutors for
              <span className="text-green-400 font-semibold"> Local</span>,
              <span className="text-blue-400 font-semibold"> Cambridge</span> &
              <span className="text-orange-400 font-semibold"> Edexcel</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <button
                onClick={() => navigate('/local')}
                className="px-7 py-3.5 bg-accentBlue text-black rounded-xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-accentBlue/20 flex items-center gap-2"
              >
                <BookOpen size={16} /> Explore Resources
              </button>
              <button
                onClick={() => navigate('/tutors')}
                className="px-7 py-3.5 bg-white/8 border border-white/12 text-white rounded-xl font-bold text-sm hover:bg-white/12 transition-colors flex items-center gap-2"
              >
                <Search size={16} /> Find a Tutor <ArrowRight size={14} />
              </button>
            </motion.div>
          </div>
        </section>

        <Divider />

        {/* ══ NEWS & ANNOUNCEMENTS TICKER ═══════════════════════════════════════ */}
        {ads.length > 0 && (
          <div className="w-full" style={{ marginLeft: 'calc(-50vw + 50%)', width: '100vw' }}>
            {/* top accent line */}
            <div className="h-[2px] w-full" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(45,136,255,0.6) 35%, rgba(245,158,11,0.5) 65%, transparent 100%)' }} />

            <div className="flex items-stretch overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(8,11,22,0.98) 0%, rgba(5,7,16,0.99) 100%)', backdropFilter: 'blur(24px)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', minHeight: '52px' }}>

              {/* Left — LIVE label */}
              <div className="shrink-0 flex flex-col items-center justify-center gap-1.5 px-6 py-4 min-w-[110px]"
                style={{ background: 'linear-gradient(135deg, rgba(45,136,255,0.13) 0%, rgba(45,136,255,0.04) 100%)', borderRight: '1px solid rgba(45,136,255,0.12)' }}>
                {/* pulsing dot */}
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accentBlue opacity-50" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accentBlue" />
                </span>
                <span className="text-accentBlue font-black text-[10px] uppercase tracking-[0.25em] whitespace-nowrap leading-none">LIVE</span>
              </div>

              {/* Middle — scrolling items */}
              <div className="flex-1 overflow-hidden relative">
                {/* fade left */}
                <div className="absolute left-0 top-0 bottom-0 w-14 z-10 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, rgba(5,7,16,1) 0%, transparent 100%)' }} />
                {/* fade right */}
                <div className="absolute right-0 top-0 bottom-0 w-14 z-10 pointer-events-none"
                  style={{ background: 'linear-gradient(270deg, rgba(5,7,16,1) 0%, transparent 100%)' }} />

                <div className="news-ticker-track flex items-center h-full">
                  {[...ads, ...ads, ...ads].map((ad, i) => (
                    <span key={i} className="inline-flex items-center shrink-0 gap-0">
                      <span className="inline-flex items-center gap-3 px-8 py-4">
                        {/* type pill */}
                        {ad.type === 'news' ? (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md bg-amber-500/20 text-amber-400 border border-amber-500/30">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                            News
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md bg-accentBlue/20 text-accentBlue border border-accentBlue/30">
                            <span className="w-1.5 h-1.5 rounded-full bg-accentBlue shrink-0" />
                            Info
                          </span>
                        )}
                        {/* title */}
                        <span className="text-white font-bold text-[14px] tracking-wide whitespace-nowrap">{ad.title}</span>
                        {/* divider */}
                        <span className="text-white/20 font-light text-lg leading-none">|</span>
                        {/* description */}
                        <span className="text-gray-300 text-[13px] whitespace-nowrap">{ad.description || ad.text}</span>
                      </span>
                      {/* separator dot */}
                      <span className="text-white/12 shrink-0 text-[8px]">◆</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Right — date */}
              <div className="shrink-0 hidden md:flex flex-col items-center justify-center px-5 py-4 gap-0.5"
                style={{ borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-[11px] font-bold text-gray-300 whitespace-nowrap">
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span className="text-[10px] text-gray-600 whitespace-nowrap">
                  {new Date().toLocaleDateString('en-US', { year: 'numeric' })}
                </span>
              </div>
            </div>

            {/* bottom accent line */}
            <div className="h-[2px] w-full" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(45,136,255,0.25) 35%, rgba(245,158,11,0.2) 65%, transparent 100%)' }} />
          </div>
        )}

        {/* ══ SCROLL DOWN INDICATOR ═════════════════════════════════════════════ */}
        <div className="flex flex-col items-center justify-center py-6 gap-2 select-none pointer-events-none">
          {/* mouse shell */}
          <div className="scroll-mouse relative w-6 h-9 rounded-full border-2 border-white/20 flex items-start justify-center pt-1.5"
            style={{ boxShadow: '0 0 12px rgba(45,136,255,0.15)' }}>
            {/* scrolling dot inside mouse */}
            <span className="scroll-dot w-1 h-1.5 rounded-full bg-accentBlue/80" />
          </div>
          {/* chevrons */}
          <div className="flex flex-col items-center gap-0.5">
            <ChevronRight size={12} className="scroll-chev1 rotate-90 text-white/20" />
            <ChevronRight size={12} className="scroll-chev2 rotate-90 text-white/30" />
            <ChevronRight size={12} className="scroll-chev3 rotate-90 text-accentBlue/50" />
          </div>
        </div>

        {/* ══ SYLLABUSES ════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-6 py-14">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}>
            <motion.div variants={fadeUp} className="text-center mb-10">
              <p className="text-accentBlue text-[10px] font-bold uppercase tracking-[0.2em] mb-2">Choose Your Path</p>
              <h2 className="text-3xl font-extrabold text-white">Select Your Syllabus</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {SYLLABUSES.map((s) => (
                <motion.div
                  key={s.name} variants={fadeUp}
                  whileHover={{ y: -6, scale: 1.01 }}
                  onClick={() => navigate(s.path)}
                  className={`cursor-pointer bg-gradient-to-br ${s.accent} border ${s.border} rounded-2xl p-7 group transition-all duration-300 hover:shadow-2xl`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <span className={`inline-block text-[10px] px-2.5 py-1 border rounded-full font-bold uppercase tracking-wider mb-2.5 ${s.badgeColor}`}>
                        {s.tag}
                      </span>
                      <h3 className="text-xl font-extrabold text-white">{s.name}</h3>
                    </div>
                    <GraduationCap size={28} className={`${s.iconColor} group-hover:scale-110 transition-transform shrink-0 mt-0.5`} />
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{s.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {s.levels.map((l) => (
                      <span key={l} className={`text-xs px-3 py-1 border rounded-full font-semibold ${s.badgeColor}`}>{l}</span>
                    ))}
                  </div>
                  <div className={`flex items-center gap-1.5 font-bold text-xs ${s.iconColor} group-hover:gap-2.5 transition-all`}>
                    Explore <ChevronRight size={14} />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <Divider />

        {/* ══ SERVICES ══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-6 py-14">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}>
            <motion.div variants={fadeUp} className="text-center mb-10">
              <p className="text-accentBlue text-[10px] font-bold uppercase tracking-[0.2em] mb-2">Everything You Need</p>
              <h2 className="text-3xl font-extrabold text-white">Platform Services</h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {SERVICES.map((svc) => (
                <motion.div
                  key={svc.name} variants={fadeUp}
                  whileHover={{ y: -5 }}
                  onClick={() => navigate(svc.path)}
                  className="cursor-pointer bg-white/4 border border-white/8 rounded-2xl p-6 hover:border-white/16 hover:bg-white/6 transition-all duration-300 group"
                >
                  <div className={`w-10 h-10 rounded-xl ${svc.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <svc.icon size={19} className={svc.color} />
                  </div>
                  <h3 className="font-bold text-white text-sm mb-1">{svc.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{svc.desc}</p>
                  <div className={`flex items-center gap-1 mt-3 text-xs font-semibold ${svc.color} opacity-0 group-hover:opacity-100 transition-opacity`}>
                    Go <ArrowRight size={11} />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>


        <style>{`
          /* ── ticker ── */
          .news-ticker-track {
            display: flex;
            animation: news-scroll 60s linear infinite;
            width: max-content;
          }
          .news-ticker-track:hover { animation-play-state: paused; cursor: default; }
          @keyframes news-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }

          /* ── scroll indicator ── */
          @keyframes scroll-dot {
            0%   { transform: translateY(0);    opacity: 1;   }
            60%  { transform: translateY(10px); opacity: 0.2; }
            100% { transform: translateY(0);    opacity: 1;   }
          }
          .scroll-dot { animation: scroll-dot 1.8s ease-in-out infinite; }

          @keyframes chev-fade {
            0%, 100% { opacity: 0.15; }
            50%       { opacity: 1;   }
          }
          .scroll-chev1 { animation: chev-fade 1.8s ease-in-out infinite 0s;    }
          .scroll-chev2 { animation: chev-fade 1.8s ease-in-out infinite 0.25s; }
          .scroll-chev3 { animation: chev-fade 1.8s ease-in-out infinite 0.5s;  }

          @keyframes mouse-glow {
            0%, 100% { box-shadow: 0 0 10px rgba(45,136,255,0.10); }
            50%       { box-shadow: 0 0 22px rgba(45,136,255,0.30); }
          }
          .scroll-mouse { animation: mouse-glow 1.8s ease-in-out infinite; }
        `}</style>
      </div>
    </>
  );
};

export default HomePage;
